import { Badge } from '@talentmesh/ui/components/badge';
import { Label } from '@talentmesh/ui/components/label';
import { RadioGroupItem } from '@talentmesh/ui/components/radio-group';
import { AtsJobResponse, AtsJobStatus } from '../../api/useAtsJobs';
import toDateString from '@/Utils/DateHelper';
import UIStrings from '@/Utils/UIStrings';

const statusLabels: Record<AtsJobStatus, string> = {
    Open: UIStrings.AtsJobStatusOpen,
    Closed: UIStrings.AtsJobStatusClosed,
    Draft: UIStrings.AtsJobStatusDraft,
    Archived: UIStrings.AtsJobStatusArchived,
    Unknown: UIStrings.AtsJobStatusUnknown,
};

const statusVariants: Record<AtsJobStatus, 'default' | 'secondary' | 'outline'> = {
    Open: 'default',
    Closed: 'secondary',
    Draft: 'outline',
    Archived: 'outline',
    Unknown: 'outline',
};

const formatOpenedAt = (openedAt: string | null): string | null => {
    if (!openedAt) {
        return null;
    }

    return toDateString(new Date(openedAt));
};

interface AtsJobOptionProps {
    job: AtsJobResponse;
}

/** One selectable job row: the radio, the job's name and status, and a line of details. */
const AtsJobOption = ({ job }: AtsJobOptionProps) => {
    const openedAt = formatOpenedAt(job.openedAt);
    const details = [job.jobCode, job.department, job.location].filter(Boolean).join(' · ');

    return (
        <Label
            htmlFor={`ats-job-${job.id}`}
            className="hover:bg-muted/50 has-[button[data-state=checked]]:border-primary flex cursor-pointer items-center gap-3 rounded-md border p-3 font-normal"
            data-cy={`AtsJobOption-${job.id}`}
        >
            <RadioGroupItem value={job.id} id={`ats-job-${job.id}`} />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-center gap-2">
                    <span className="text-secondary-foreground truncate text-sm font-medium">
                        {/* An ATS can report a job with no name; the code keeps the row identifiable. */}
                        {job.name || job.jobCode || job.id}
                    </span>
                    <Badge variant={statusVariants[job.status]}>{statusLabels[job.status]}</Badge>
                </div>
                {details && <span className="text-muted-foreground truncate text-xs">{details}</span>}
                {openedAt && (
                    <span className="text-muted-foreground text-xs">{`${UIStrings.AtsJobOpenedOn} ${openedAt}`}</span>
                )}
            </div>
        </Label>
    );
};

export default AtsJobOption;
